import React from 'react'

import type { OnChangeFn, SortingState, Updater } from '@tanstack/react-table'
import type { NavigateFunction } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import type { PageChangeDirection } from '@constants'

type UseTableReturnType = Partial<{
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  onRowsPerPageChange: (value: number) => void
  onPageChange: PageChangeFunction
  onPageReset: VoidFunction
}>

export type PageChangePayload = {
  url: string
  direction?: PageChangeDirection
}
export type PageChangeFunction = (payload: PageChangePayload) => void

export type UpdateOrderPayload = {
  navigate: NavigateFunction
  order: OrderOptions
  orderBy: string
}
export type UpdateOrderFunction = (payload: UpdateOrderPayload) => void

export type UpdateRowsPerPagePayload = {
  navigate: NavigateFunction
  pageSize: number
}
export type UpdateRowsPerPageFunction = (payload: UpdateRowsPerPagePayload) => void

type UseTableControlsProps = Partial<{
  order: OrderOptions
  orderBy: DefaultSorting['orderBy']
  updateOrder: UpdateOrderFunction
  updateRowsPerPage: UpdateRowsPerPageFunction
  handlePageChange: PageChangeFunction
  handlePageReset: (payload: { navigate: NavigateFunction }) => void
}>

/**
 * Hook for creating table control functions and configs. It will only return with valid values if you provide necessary input parameters.
 *
 * For sorting you need to provide `order`, `orderBy` and `updateOrder`
 *
 * @param {UseTableControlsProps} props
 */
export function useTableControls({
  order,
  orderBy,
  updateOrder,
  updateRowsPerPage,
  handlePageChange,
  handlePageReset,
}: UseTableControlsProps): UseTableReturnType {
  const navigate = useNavigate()

  return React.useMemo(() => {
    // declare variables for return values
    let sorting: SortingState | undefined = undefined
    let onSortingChange, onRowsPerPageChange, onPageChange, onPageReset

    if (order && orderBy && updateOrder) {
      sorting = [{ id: orderBy, desc: order === 'desc' }]

      onSortingChange = (updaterOrValue: Updater<SortingState>) => {
        const newSorting =
          typeof updaterOrValue === 'function' ? updaterOrValue(sorting as SortingState) : updaterOrValue
        // there is no multi sorting so we only need the first item
        const [{ id, desc } = { id: '', desc: false }] = newSorting
        updateOrder({ navigate, order: desc ? 'desc' : 'asc', orderBy: id })
      }
    }

    if (updateRowsPerPage) {
      // change rows per page for TableCursorPagination component
      onRowsPerPageChange = (value: number) => {
        updateRowsPerPage({ navigate, pageSize: value })
      }
    }

    if (handlePageChange) {
      // change page for TableCursorPagination component
      onPageChange = (payload: PageChangePayload) => {
        handlePageChange(payload)
      }
    }

    if (handlePageReset) {
      onPageReset = () => {
        handlePageReset({ navigate })
      }
    }

    return {
      sorting,
      onSortingChange,
      onRowsPerPageChange,
      onPageChange,
      onPageReset,
    } as UseTableReturnType
  }, [updateOrder, updateRowsPerPage, handlePageChange, handlePageReset, orderBy, order, navigate])
}
